import { create } from 'zustand';
import type { BibleHighlight, HighlightFamily } from '@/types/highlight';
import * as db from '@/lib/db';
import { highlightId } from '@/lib/highlight-utils';
import {
  queueHighlightDeleteMutations,
  queueHighlightSetMutations,
  requestHighlightLifecycleSync,
} from '@/lib/sync-highlights';
import { assertCoreWriteAllowed, isCoreReadOnly } from '@/lib/core-cutover';
import { getContentOwnerBinding } from '@/lib/content-owner';
import { highlightFromCompleteDocument } from '@/lib/content-lifecycle/contracts';
import {
  getOwnerContentEntities,
  hasContentLifecycleEntityBootstrap,
} from '@/lib/content-lifecycle/store';
import { getAuthEpoch, getAuthUserId, waitForAuthResolution } from '@/lib/keepsake/epochs';
import type { CompleteHighlightDocument } from '@/types/content-lifecycle';
import {
  ContentHydrationStaleError,
  recordContentHydrationJoinedLoad,
  runContentHydrationPublishProbe,
} from '@/lib/content-hydration';

// In-memory store for Bible highlights, mirroring src/lib/bookmark-store.ts.
// `highlights` holds only LIVE rows (tombstones filtered) so consumers (the
// reader wash, the journal) render it directly. Delete is a soft tombstone that
// rides the outbox; confirmed-synced tombstones are purged from IDB on
// loadHighlights. Silent sync, no UI sync state, same posture as bookmarks.
//
// DIVERGENCE from bookmark-store: bookmarks `addBookmark` PREPENDS a fresh row;
// highlights `setHighlight` UPSERTS by the deterministic id (re-highlighting a
// verse, or flipping a tombstone back), and PRESERVES the Chunk E
// personWho/fulfilledAt fields on re-highlight (pass-1 LOW 1).

export type HighlightSetInput = {
  bookSlug: string;
  chapter: number;
  verse: number;
  family: HighlightFamily;
  sublabel?: string | null;
  reference: string;
};

interface HighlightState {
  highlights: BibleHighlight[];
  isLoading: boolean;

  loadHighlights: () => Promise<void>;
  setHighlight: (h: HighlightSetInput) => Promise<void>;
  setHighlightsBatch: (items: HighlightSetInput[]) => Promise<void>;
  removeHighlight: (id: string) => Promise<void>;
  removeHighlightsBatch: (ids: string[]) => Promise<void>;
}

let highlightLoadRequestId = 0;
let highlightLoadInFlight: { key: string; promise: Promise<void> } | null = null;
let highlightMutationVersion = 0;
const highlightMutationVersions = new Map<string, number>();

function markHighlightMutation(id: string): void {
  highlightMutationVersion += 1;
  highlightMutationVersions.set(id, highlightMutationVersion);
}

function mergeHighlightMutations(
  loaded: BibleHighlight[],
  current: BibleHighlight[],
  startedAtMutationVersion: number,
): BibleHighlight[] {
  const changed = new Set(
    [...highlightMutationVersions]
      .filter(([, version]) => version > startedAtMutationVersion)
      .map(([id]) => id),
  );
  if (changed.size === 0) return loaded;
  return [
    ...loaded.filter((highlight) => !changed.has(highlight.id)),
    ...current.filter((highlight) => changed.has(highlight.id)),
  ].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export const useHighlightStore = create<HighlightState>((set, get) => ({
  highlights: [],
  isLoading: false,

  loadHighlights: async () => {
    await waitForAuthResolution();
    const readOnly = isCoreReadOnly();
    const ownerId = getAuthUserId();
    const authEpoch = getAuthEpoch();
    const loadKey = `${ownerId ?? 'signed-out'}\n${authEpoch}\n${readOnly ? 'read-only' : 'writable'}`;
    if (highlightLoadInFlight?.key === loadKey) {
      recordContentHydrationJoinedLoad('highlights');
      return highlightLoadInFlight.promise;
    }
    const requestId = ++highlightLoadRequestId;
    const startedAtMutationVersion = highlightMutationVersion;
    set({ isLoading: true });
    const promise = (async () => {
      try {
      const binding = ownerId ? await getContentOwnerBinding() : null;
      const authorizedOwner = Boolean(ownerId && binding === ownerId);
      let all: BibleHighlight[];
      if (!authorizedOwner) {
        all = [];
      } else if (readOnly) {
        all = await db.getAllHighlights();
      } else {
        const [entities, legacy, initialized] = await Promise.all([
          getOwnerContentEntities(ownerId as string),
          db.getAllHighlights(),
          hasContentLifecycleEntityBootstrap(ownerId as string, 'highlight'),
        ]);
        const lifecycle = entities
          .filter((entity) => entity.entityType === 'highlight' && entity.visibleDocument !== null)
          .map((entity) =>
            highlightFromCompleteDocument(entity.visibleDocument as CompleteHighlightDocument),
          );
        all = !initialized && lifecycle.length === 0 ? legacy : lifecycle;
      }
      const live = all
        .filter((h) => !h.deletedAt)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
      await runContentHydrationPublishProbe('highlights');
      const finalBinding = ownerId ? await getContentOwnerBinding() : null;
      const finalAuthorizedOwner = Boolean(ownerId && finalBinding === ownerId);
      const canPublish =
        requestId === highlightLoadRequestId &&
        get().isLoading &&
        isCoreReadOnly() === readOnly &&
        getAuthUserId() === ownerId &&
        getAuthEpoch() === authEpoch &&
        finalAuthorizedOwner === authorizedOwner;
      if (canPublish) {
        set({
          highlights: authorizedOwner
            ? mergeHighlightMutations(live, get().highlights, startedAtMutationVersion)
            : [],
        });
      } else {
        throw new ContentHydrationStaleError('owner-auth-or-request-changed');
      }
      } finally {
        if (requestId === highlightLoadRequestId) set({ isLoading: false });
      }
    })();
    highlightLoadInFlight = { key: loadKey, promise };
    try {
      await promise;
    } finally {
      if (highlightLoadInFlight?.promise === promise) highlightLoadInFlight = null;
    }
  },

  setHighlight: async (h) => {
    await get().setHighlightsBatch([h]);
  },

  setHighlightsBatch: async (items) => {
    assertCoreWriteAllowed('store:highlight:set');
    if (items.length === 0) return;
    const now = new Date().toISOString();
    const fallbacks = await Promise.all(items.map(async (h): Promise<BibleHighlight> => {
      const id = highlightId(h.bookSlug, h.chapter, h.verse);
      const existing = await db.getHighlight(id) ?? get().highlights.find((item) => item.id === id);
      return {
        id,
        bookSlug: h.bookSlug,
        chapter: h.chapter,
        verse: h.verse,
        family: h.family,
        sublabel: h.sublabel ?? null,
        reference: h.reference,
        personWho: existing?.personWho ?? null,
        fulfilledAt: existing?.fulfilledAt ?? null,
        deletedAt: null,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        syncedAt: null,
      };
    }));
    const nextRows = await queueHighlightSetMutations(fallbacks);
    for (const row of nextRows) markHighlightMutation(row.id);
    const nextById = new Map(nextRows.map((row) => [row.id, row]));
    set((s) => ({
      highlights: [
        ...nextRows.filter((row) => !s.highlights.some((item) => item.id === row.id)),
        ...s.highlights.map((row) => nextById.get(row.id) ?? row),
      ],
    }));
    requestHighlightLifecycleSync();
  },

  removeHighlight: async (id) => {
    await get().removeHighlightsBatch([id]);
  },

  removeHighlightsBatch: async (ids) => {
    assertCoreWriteAllowed('store:highlight:remove');
    if (ids.length === 0) return;
    const uniqueIds = [...new Set(ids)];
    const now = new Date().toISOString();
    const committedIds = await queueHighlightDeleteMutations(uniqueIds, now);
    for (const id of committedIds) markHighlightMutation(id);
    const removed = new Set(committedIds);
    set({ highlights: get().highlights.filter((h) => !removed.has(h.id)) });
    requestHighlightLifecycleSync();
  },
}));
